import React, { useState } from 'react';
import { FaDropbox, FaDrupal, FaGoogleDrive, FaHubspot, FaSalesforce, FaShopify, FaTypo3, FaWebflow, FaWordpress } from "react-icons/fa6";
import { CgMonday } from "react-icons/cg";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { GrOnedrive } from "react-icons/gr";
import { SiBigcommerce, SiBox, SiCalendly, SiGooglecalendar, SiWoocommerce, SiZoho } from "react-icons/si";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi, Integration, IntegrationRequest } from '../lib/tenant-api';
import { toast } from 'react-toastify';
import { Plus, Link, Check, X, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import IntegrationConfigModal from './IntegrationConfigModal';
import { ConfirmationModal, PageLoader } from './common';
import { useGoogleLogin } from '@react-oauth/google';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="mt-8">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center justify-between w-full p-4 text-left border  transition-all duration-200 ease-in-out focus:outline-none ${isExpanded
          ? 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100 focus:border-indigo-300'
          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 focus:border-gray-300'
          }`}
      >
        <h3 className={`text-lg font-medium transition-colors duration-200 ${isExpanded ? 'text-indigo-900' : 'text-gray-900'
          }`}>
          {title}
        </h3>
        {isExpanded ? (
          <ChevronUp className={`h-5 w-5 transition-colors duration-200 ${isExpanded ? 'text-indigo-600' : 'text-gray-500'
            }`} />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 transition-all duration-200 ease-in-out">
          {children}
        </div>
      )}
    </div>
  );
};

interface IntegrationCardProps {
  name: string;
  description: string;
  bgColor: string;
  comingSoon?: boolean;
  integration?: Integration,
  icon?: React.ComponentType<any>;
  googelConnect?: () => void
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  name,
  description,
  bgColor,
  icon,
  comingSoon = true,
  integration,
  googelConnect
}) => {
  const queryClient = useQueryClient();
  const [isWordPressModalOpen, setIsWordPressModalOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [integrationType, setIntegrationType] = useState<string>(name.toLowerCase().replace('_', '-'));
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);

  const testIntegrationMutation = useMutation({
    mutationFn: tenantApi.testIntegration,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || 'Integration connection successful!');
      } else {
        toast.error(data.error || 'Integration connection failed');
      }
    },
    onError: (error: any) => {
      toast.error(`Failed to test integration: ${error.message}`);
    },
  });

  const deleteIntegrationMutation = useMutation({
    mutationFn: tenantApi.deleteIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Integration deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete integration: ${error.message}`);
    },
  });


  const handleAddIntegration = (name: string) => {
    setIntegrationType(name);
    setEditingIntegration(null);
    setIsWordPressModalOpen(true);
  };

  const handleEditIntegration = (integration: Integration) => {
    setEditingIntegration(integration);
    setIsWordPressModalOpen(true);
    setIntegrationType(integration.integration_type.toLowerCase());
  };

  const handleTestIntegration = (integrationId: string) => {
    testIntegrationMutation.mutate(integrationId);
  };

  const handleDeleteIntegration = (integration: Integration) => {
    setIntegrationToDelete(integration);
    setDeleteModalOpen(true);
  };

  const confirmDeleteIntegration = () => {
    if (integrationToDelete) {
      deleteIntegrationMutation.mutate(integrationToDelete.id);
      setDeleteModalOpen(false);
      setIntegrationToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setIntegrationToDelete(null);
  };

  const getStatusIcon = (integration: Integration) => {
    if (integration.is_enabled) {
      return <Check className="h-5 w-5 text-green-500" />;
    } else {
      return <X className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (integration: Integration) => {
    if (integration.is_enabled) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Enabled
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Disabled
        </span>
      );
    }
  };

  return (
    <div className={`bg-white border border-gray-200 flex flex-col p-6 shadow-sm ${comingSoon ? 'opacity-50' : ''}`} >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className={`w-10 h-10 ${bgColor}  flex-shrink-0 flex items-center justify-center`}>
            {icon ? React.createElement(icon, { className: 'h-6 w-6 text-white' }) : <Link className="h-6 w-6 text-white" />}
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">{name.replace('_', ' ')}</h3>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        {
          integration ? getStatusIcon(integration)
            :
            comingSoon && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap flex-shrink-0">
                Coming Soon
              </span>
            )}
      </div>

      {integration ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">
              {integration.name}
            </span>
            {getStatusText(integration)}
          </div>

          {integration.last_sync && (
            <p className="text-xs text-gray-500">
              Last sync: {new Date(integration.last_sync).toLocaleDateString()}
            </p>
          )}

          <div className="flex space-x-3">
            <button
              onClick={() => handleTestIntegration(integration.id)}
              disabled={testIntegrationMutation.isLoading}
              className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {testIntegrationMutation.isLoading ? 'Testing...' : 'Test Connection'}
            </button>
            {/* {integration.integration_type !== 'google_drive' && ( */}
            <button
              onClick={() => handleEditIntegration(integration)}
              className="inline-flex items-center p-2 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Settings className="h-4 w-4" />
            </button>
            {/* )} */}

            <button
              onClick={() => handleDeleteIntegration(integration)}
              disabled={deleteIntegrationMutation.isLoading}
              className="inline-flex items-center p-2 border border-red-300 shadow-sm text-sm font-medium  text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        !comingSoon && <div className="space-y-3 flex flex-col justify-end mt-auto flex-1">

          <button
            onClick={() => {
              console.log("googelConnect", googelConnect)
              if (googelConnect !== undefined) {
                googelConnect();
              } else {
                handleAddIntegration(name.toLowerCase())
              }
            }
            }
            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium  text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mt-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Connect {name.replace('_', ' ')}
          </button>
        </div>
      )}

      {/* WordPress Config Modal */}
      <IntegrationConfigModal
        isOpen={isWordPressModalOpen}
        onClose={() => {
          setIsWordPressModalOpen(false);
          setEditingIntegration(null);
        }}
        integration={editingIntegration}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['integrations'] });
          setIsWordPressModalOpen(false);
          setEditingIntegration(null);
        }}
        integrationType={integrationType}
      />

      {/* Delete Integration Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteIntegration}
        title="Delete Integration"
        message={`Are you sure you want to delete the integration "${integrationToDelete?.name || 'Unknown'}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        isLoading={deleteIntegrationMutation.isLoading}
      />
    </div>

  );
};

const IntegrationsSection: React.FC = () => {
  const queryClient = useQueryClient();


  const { data: integrations = [], isLoading } = useQuery<Integration[]>({
    queryKey: ['integrations'],
    queryFn: tenantApi.getIntegrations,
  });

  const createGoogleIntegrationMutation = useMutation({
    mutationFn: tenantApi.createIntegration,
    onSuccess: () => {
      toast.success(`Google integration created successfully!`);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (error: any) => {
      toast.error(`Failed to create integration: ${error.message}`);
    },
  });

  const connectGoogle = useGoogleLogin({
    onSuccess: codeResponse => {
      const integrationData: IntegrationRequest = {
        integration_type: 'google_drive',
        name: "Google Drive",
        config: codeResponse,
        is_enabled: true,
      };
      createGoogleIntegrationMutation.mutate(integrationData)
    },
    onError: () => {
      console.log('Login Failed');
    },
    scope: 'openid email profile https://www.googleapis.com/auth/drive.readonly',
    flow: 'auth-code',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-gray-50">
        <PageLoader fullScreen={false} />
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="border-t border-gray-200 pt-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Integrations</h2>
        <p className="text-sm text-gray-600 mb-6">
          Connect your CMS and platforms to automatically import content into your AI assistant.
        </p>

        {/* Priority Integrations Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Featured Integrations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Google Integration Card */}


            <IntegrationCard
              name="Google_Drive"
              description="Connect your Google Drive to import documents."
              bgColor="bg-indigo-500"
              comingSoon={false}
              integration={integrations.find(int => int.integration_type === 'google_drive')}
              googelConnect={() => connectGoogle()}
              icon={FaGoogleDrive}
            />

            {/* Shopify Integration Card */}
            <IntegrationCard
              name="WordPress"
              description="Connect your WordPress site to import products and posts."
              bgColor="bg-gray-900"
              comingSoon={false}
              integration={integrations.find(int => int.integration_type === 'wordpress')}
              icon={FaWordpress}
            />
            <IntegrationCard
              name="Shopify"
              description="Connect your Shopify to import products, knowledge Base docs."
              bgColor="bg-green-500"
              comingSoon={false}
              integration={integrations.find(int => int.integration_type === 'shopify')}
              icon={FaShopify}
            />

            {/* Webflow Integration Card */}
            <IntegrationCard
              name="Webflow"
              description="Connect your webflow account to import content."
              bgColor="bg-indigo-600"
              comingSoon={false}
              integration={integrations.find(int => int.integration_type === 'webflow')}
              icon={FaWebflow}
            />
            {/* HubSpot Integration Card */}
            <IntegrationCard
              name="HubSpot"
              description="Connect your HubSpot account to import leads."
              bgColor="bg-orange-500"
              comingSoon={false}
              integration={integrations.find(int => int.integration_type === 'hubspot')}
              icon={FaHubspot}
            />
            {/* Webflow Integration Card */}
            <IntegrationCard
              name="Salesforce"
              description="Connect your Salesforce account to import leads and contacts."
              bgColor="bg-indigo-500"
              comingSoon={false}
              integration={integrations.find(int => int.integration_type === 'salesforce')}
              icon={FaSalesforce}
            />
          </div>
        </div>

        {/* Collapsible Category Sections */}
        <CollapsibleSection title="Content Management Systems">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <IntegrationCard
              name="TYPO3"
              description="Import content and pages"
              bgColor="bg-orange-600"
              icon={FaTypo3}
            />
            <IntegrationCard
              name="Drupal"
              description="Import content and modules"
              bgColor="bg-sky-500"
              icon={FaDrupal}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Shopping Carts">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <IntegrationCard
              name="BigCommerce"
              description="Import products and orders"
              bgColor="bg-slate-700"
              icon={SiBigcommerce}
            />
            <IntegrationCard
              name="WooCommerce"
              description="Import products and orders"
              bgColor="bg-purple-700"
              icon={SiWoocommerce}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Customer Relationship Management">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <IntegrationCard
              name="Salesforce"
              description="Import contacts, leads, and opportunities"
              bgColor="bg-indigo-500"
              icon={FaSalesforce}
            />
            <IntegrationCard
              name="Zoho"
              description="Import contacts and deals"
              bgColor="bg-red-500"
              icon={SiZoho}
            />
            <IntegrationCard
              name="Monday.com"
              description="Import tasks and project data"
              bgColor="bg-red-500"
              icon={CgMonday}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Calendaring Apps">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <IntegrationCard
              name="Calendly"
              description="Import scheduling and availability"
              bgColor="bg-indigo-600"
              icon={SiCalendly}
            />
            <IntegrationCard
              name="Google Calendar"
              description="Import events and schedules"
              bgColor="bg-indigo-500"
              icon={SiGooglecalendar}
            />
            <IntegrationCard
              name="Outlook"
              description="Import calendar and contacts"
              bgColor="bg-indigo-500"
              icon={PiMicrosoftOutlookLogoFill}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Marketing Asset Repositories">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <IntegrationCard
              name="Dropbox"
              description="Import files and folders"
              bgColor="bg-indigo-600"
              icon={FaDropbox}
            />
            <IntegrationCard
              name="Box"
              description="Import enterprise content"
              bgColor="bg-indigo-700"
              icon={SiBox}
            />
            <IntegrationCard
              name="OneDrive"
              description="Import Microsoft files"
              bgColor="bg-indigo-600"
              icon={GrOnedrive}
            />
          </div>
        </CollapsibleSection>
      </div>


    </div>
  );
};

export default IntegrationsSection;
